import { useEffect, useState } from 'react'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import ContentCopy from '@mui/icons-material/ContentCopy'
import { Link as RouterLink } from 'react-router-dom'
import { toast } from 'react-toastify'
import { mediaURL } from 'src/consumer/services'
import {
  approveTherapistApplicant,
  rejectTherapistApplicant,
} from 'src/supportPortal/services/practitionerOnboardingService'
import { getSPDataWithParams } from 'src/supportPortal/services/services'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'

// Phase 14c — applicationStage values mirror Meta.APPLICATION_STAGE_* on the server.
const STAGE_LABEL = {
  stage_1_applied: { label: 'New application', color: 'default' },
  stage_1_approved: { label: 'Awaiting Stage 2', color: 'info' },
  stage_2_in_progress: { label: 'Stage 2 in progress', color: 'warning' },
  stage_2_completed: { label: 'Stage 2 done', color: 'success' },
  rejected: { label: 'Rejected', color: 'error' },
}

/**
 * Lists self-submitted therapist applicants for a discipline.
 *
 * Phase 5c — backed by /api/secure/cc/support/practitioner-onboarding/applicants?discipline=...
 * Each row shows the applicant identity, contact, supplied qualifications, and a "Review &
 * onboard" button that links to the support onboarding flow with the practitioner-id pre-filled.
 */
export default function TherapistApplicantsListing({ discipline, disciplineLabel }) {
  const [state, setState] = useState({ loading: true, error: '', data: [] })
  const [approvingId, setApprovingId] = useState(null)
  // Phase 14c — reject dialog state. We hold the entire applicant object so the dialog can show
  // their name and we don't need a second lookup on confirm.
  const [rejectTarget, setRejectTarget] = useState(null)
  const [rejectReason, setRejectReason] = useState('')
  const [rejecting, setRejecting] = useState(false)
  const isRejectedTab = discipline === 'rejected'

  async function loadApplicants() {
    setState((prev) => ({ ...prev, loading: true }))
    const resp = await getSPDataWithParams(
      '/api/secure/cc/support/practitioner-onboarding/applicants',
      { discipline },
    )
    if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
      const list = getDataMapFromCommonResponse(resp)?.applicants || []
      setState({ loading: false, error: '', data: list })
    } else {
      setState({
        loading: false,
        error: resp?.data?.message || 'Could not load applicants.',
        data: [],
      })
    }
  }

  useEffect(() => {
    let cancelled = false
    setState({ loading: true, error: '', data: [] })
    ;(async () => {
      const resp = await getSPDataWithParams(
        '/api/secure/cc/support/practitioner-onboarding/applicants',
        { discipline },
      )
      if (cancelled) return
      if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
        const list = getDataMapFromCommonResponse(resp)?.applicants || []
        setState({ loading: false, error: '', data: list })
      } else {
        setState({
          loading: false,
          error: resp?.data?.message || 'Could not load applicants.',
          data: [],
        })
      }
    })()
    return () => {
      cancelled = true
    }
  }, [discipline])

  async function handleApprove(applicant) {
    if (!applicant?.practitionerId) return
    setApprovingId(applicant.practitionerId)
    // For uncategorized applicants, we don't pass a discipline — the agent will pick one in the
    // onboarding form. For tagged ones, pass the discipline so the approval stamps the right
    // approvedTherapyDiscipline.
    const passDiscipline =
      discipline && discipline !== 'uncategorized' && discipline !== 'rejected' ? discipline : ''
    const resp = await approveTherapistApplicant({
      practitionerId: applicant.practitionerId,
      discipline: passDiscipline,
    })
    setApprovingId(null)
    if (resp.ok) {
      toast.success(`${applicant.name || applicant.practitionerId} approved.`)
      // Refresh — the now-approved applicant drops out of the pending list.
      loadApplicants()
    } else {
      toast.error(resp.message || 'Could not approve applicant.')
    }
  }

  function openRejectDialog(applicant) {
    setRejectTarget(applicant)
    setRejectReason('')
  }

  function closeRejectDialog() {
    if (rejecting) return
    setRejectTarget(null)
    setRejectReason('')
  }

  async function handleRejectConfirm() {
    if (!rejectTarget?.practitionerId) return
    const trimmed = rejectReason.trim()
    if (!trimmed) {
      toast.error('Reason is required.')
      return
    }
    setRejecting(true)
    const resp = await rejectTherapistApplicant({
      practitionerId: rejectTarget.practitionerId,
      reason: trimmed,
    })
    setRejecting(false)
    if (resp.ok) {
      toast.success(`${rejectTarget.name || rejectTarget.practitionerId} rejected.`)
      setRejectTarget(null)
      setRejectReason('')
      loadApplicants()
    } else {
      toast.error(resp.message || 'Could not reject applicant.')
    }
  }

  async function copyStage2Link(applicant) {
    const path = applicant?.stage2EntryPath
    if (!path) return
    const fullUrl = `${window.location.origin}${path}`
    try {
      await navigator.clipboard.writeText(fullUrl)
      toast.success('Stage 2 link copied.')
    } catch {
      toast.error('Could not copy link — copy manually: ' + fullUrl)
    }
  }

  if (state.loading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (state.error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{state.error}</Alert>
      </Box>
    )
  }

  if (state.data.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">
          {isRejectedTab
            ? 'No rejected applicants in the past records.'
            : `No pending ${disciplineLabel || discipline} applicants right now.`}
        </Alert>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        {state.data.map((applicant) => {
          const stageMeta = STAGE_LABEL[applicant.applicationStage]
          // Treat any rejected-status applicant as rejected even if the stage tracker is missing
          // — covers legacy rows that pre-date Phase 14c.
          const isRejected =
            applicant.applicationStage === 'rejected' || applicant.kycStatus === 'rejected'
          return (
            <Paper
              key={applicant.practitionerId || applicant.userId}
              variant="outlined"
              sx={{ p: 2, borderRadius: 2 }}
            >
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
                <Avatar
                  src={applicant.profilePicId ? `${mediaURL}${applicant.profilePicId}` : undefined}
                  alt={applicant.name || applicant.practitionerId}
                  sx={{ width: 48, height: 48 }}
                >
                  {(applicant.name || '?').charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {applicant.name || applicant.practitionerId}
                    </Typography>
                    {stageMeta && (
                      <Chip size="small" label={stageMeta.label} color={stageMeta.color} />
                    )}
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {applicant.email || applicant.mobile || 'No contact info'}
                    {applicant.kycStatus && ` · ${applicant.kycStatus}`}
                  </Typography>
                  {applicant.shortDesc && (
                    <Typography variant="body2" sx={{ mt: 0.5 }} color="text.secondary">
                      {applicant.shortDesc}
                    </Typography>
                  )}
                  {isRejected && applicant.rejectionReason && (
                    <Alert severity="error" variant="outlined" sx={{ mt: 1 }}>
                      <strong>Reason:</strong> {applicant.rejectionReason}
                    </Alert>
                  )}
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                    {(applicant.qualifications || []).slice(0, 4).map((q) => (
                      <Chip key={`${applicant.practitionerId}-q-${q}`} size="small" label={q} variant="outlined" />
                    ))}
                    {applicant.mandatoryCredentialDocFileId && (
                      <Chip size="small" color="success" variant="outlined" label="Doc uploaded" />
                    )}
                    {applicant.signatureMediaId && (
                      <Chip size="small" color="success" variant="outlined" label="Signature on file" />
                    )}
                    {applicant.stage2EntryPath && (
                      <Tooltip title="Copy Stage 2 link to share with the applicant">
                        <Chip
                          size="small"
                          color="info"
                          icon={<ContentCopy sx={{ fontSize: 14 }} />}
                          label="Copy Stage 2 link"
                          onClick={() => copyStage2Link(applicant)}
                          clickable
                        />
                      </Tooltip>
                    )}
                  </Stack>
                </Box>
                <Stack direction="column" spacing={1}>
                  {!isRejected && (
                    <Button
                      variant="contained"
                      color="success"
                      disabled={approvingId === applicant.practitionerId}
                      onClick={() => handleApprove(applicant)}
                    >
                      {approvingId === applicant.practitionerId ? 'Approving…' : 'Approve'}
                    </Button>
                  )}
                  {!isRejected && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => openRejectDialog(applicant)}
                    >
                      Reject…
                    </Button>
                  )}
                  <Button
                    component={RouterLink}
                    variant="outlined"
                    size="small"
                    to={`/support-home/practitioner-management/onboarding?practitioner-id=${
                      applicant.practitionerId
                    }&type=therapist`}
                  >
                    Review in onboarding
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          )
        })}
      </Stack>

      <Dialog open={Boolean(rejectTarget)} onClose={closeRejectDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Reject{rejectTarget?.name ? ` ${rejectTarget.name}` : ' applicant'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Tell the applicant why we couldn't proceed. The reason is recorded against the
            application and shows in the Rejected tab so the team can re-open if needed.
          </DialogContentText>
          <TextField
            autoFocus
            multiline
            minRows={3}
            fullWidth
            label="Rejection reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="e.g. Couldn't verify the supplied registration number — please re-apply with a valid RCI / MCI ID."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRejectDialog} disabled={rejecting}>
            Cancel
          </Button>
          <Button
            onClick={handleRejectConfirm}
            color="error"
            variant="contained"
            disabled={rejecting || !rejectReason.trim()}
          >
            {rejecting ? 'Rejecting…' : 'Reject application'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
